/**
 * Admin API client for controller registry versions (nrs-composer private routes).
 * Requires embodiment admin or super-admin role (Firebase ID token in Authorization).
 */

/**
 * Composer API origin for the current deployment, from NEXT_PUBLIC_COMPOSER_URL
 * (set per environment in .env.prod, .env.staging, and .env.local).
 */
export function resolveComposerBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_COMPOSER_URL?.trim().replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error("Missing NEXT_PUBLIC_COMPOSER_URL for Composer API requests.");
  }
  return baseUrl;
}

/**
 * Base URL for Composer API calls from the nrs-portal web app.
 * In the browser, use same-origin `/api/composer` so requests are proxied by Next.js
 * and avoid CORS preflight to `/v1/private/*` (edge often returns 404 on OPTIONS).
 */
export function composerBrowserBaseUrl(): string {
  return typeof window !== "undefined"
    ? "/api/composer"
    : resolveComposerBaseUrl();
}

function authorizationHeader(accessToken: string): Record<string, string> {
  const t = accessToken.trim();
  // Match @neuraville/react-core-pro / EmbodimentEditDialog: raw Firebase ID token
  return t ? { Authorization: t } : {};
}

export type ControllerVersionAdminRow = {
  version_id: string;
  controller_id: string;
  version: string;
  release_channel: string;
  state: string;
  published_at?: string;
  changelog?: string;
  created_at?: string;
  updated_at?: string;
};

/**
 * Admin registry API base: `/v1/private/admin/controllers` (same-origin `/api/composer` in
 * the browser so the Next proxy egress hits Composer; edge IP allowlist applies to `/v1/private/*`).
 */
function adminControllersBaseUrl(): string {
  const base =
    typeof window !== "undefined"
      ? "/api/composer"
      : resolveComposerBaseUrl();
  return `${base.replace(/\/$/, "")}/v1/private/admin/controllers`;
}

export type ListControllerVersionsResult = {
  rows: ControllerVersionAdminRow[];
  /** `public` = admin route was 404; only published versions (read-only for edits). */
  source: "admin" | "public";
};

function publicListVersionsUrl(controllerId: string): string {
  const base =
    typeof window !== "undefined" ? "/api/composer" : resolveComposerBaseUrl();
  return `${base.replace(/\/$/, "")}/v1/public/regional/auth/user/controllers/${encodeURIComponent(
    controllerId
  )}/versions`;
}

function adminListVersionsUrl(controllerId: string): string {
  return `${adminControllersBaseUrl()}/${encodeURIComponent(controllerId)}/versions`;
}

/**
 * List version rows: tries private admin API first (all states); if that returns 404,
 * falls back to the public catalog (published only).
 */
export async function adminListControllerVersions(
  accessToken: string,
  controllerId: string
): Promise<ListControllerVersionsResult> {
  const headers = { ...authorizationHeader(accessToken) };
  const adminUrl = adminListVersionsUrl(controllerId);
  const res = await fetch(adminUrl, { headers });

  if (res.ok) {
    const data = (await res.json()) as unknown;
    if (!Array.isArray(data)) {
      throw new Error("Composer returned an unexpected response (expected a JSON array).");
    }
    return { rows: data as ControllerVersionAdminRow[], source: "admin" };
  }

  if (res.status === 404) {
    const pubUrl = publicListVersionsUrl(controllerId);
    const resPub = await fetch(pubUrl, { headers });
    if (resPub.ok) {
      const data = (await resPub.json()) as unknown;
      if (!Array.isArray(data)) {
        throw new Error("Composer returned an unexpected response (expected a JSON array).");
      }
      return { rows: data as ControllerVersionAdminRow[], source: "public" };
    }
    const pubText = await resPub.text();
    const adminText = await res.text();
    throw new Error(
      `Failed to list controller versions. Admin: 404 (${adminText.slice(0, 200)}). ` +
        `Public: ${resPub.status} (${pubText.slice(0, 200)}).`
    );
  }

  const text = await res.text();
  throw new Error(`Failed to list controller versions (${res.status}): ${text}`);
}

export type ControllerVersionPatchBody = {
  state?: string;
  release_channel?: string;
};

/**
 * Update registry state and/or release channel for one semver.
 */
export async function adminPatchControllerVersion(
  accessToken: string,
  controllerId: string,
  version: string,
  body: ControllerVersionPatchBody
): Promise<ControllerVersionAdminRow> {
  const url = `${adminControllersBaseUrl()}/${encodeURIComponent(
    controllerId
  )}/versions/${encodeURIComponent(version)}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...authorizationHeader(accessToken),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to update controller version (${res.status}): ${text}`);
  }
  return res.json() as Promise<ControllerVersionAdminRow>;
}
